/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AlertTriangle, CreditCard, Clock, X, ChevronRight, FileText, ExternalLink, RefreshCw } from 'lucide-react';
import { useInitiatePayment, useCheckPaymentStatus } from '@/hooks/api/usePayments';
import { useMySubscriptionPay } from '@/hooks/api/useMySubscription';
import { useAuthStore } from '@/stores/authStore';
import { SubscriptionSummary, OrgSubscriptionStatus } from '@/types/superadmin.types';
import { isApiError } from '@/services/api/errors';

interface PaymentAlertBannerProps {
  subscription: SubscriptionSummary | null;
  orgId: string;
  onPaymentInitiated?: (redirectUrl: string) => void;
}

type AlertType = 'renewal' | 'pastDue' | 'suspended' | 'expired' | 'trial' | null;

function getAlertType(subscription: SubscriptionSummary | null): AlertType {
  if (!subscription) return null;

  const status = subscription.status;
  const nextBillingDate = subscription.nextBillingDate ? new Date(subscription.nextBillingDate) : null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Suspended - needs immediate payment to reactivate
  if (status === 'SUSPENDED') {
    return 'suspended';
  }

  // Expired trial - needs subscription
  if (status === 'EXPIRED') {
    return 'expired';
  }

  // Past due status from backend
  if (status === 'PAST_DUE') {
    return 'pastDue';
  }

  // Trial ending soon (within 7 days)
  if (status === 'TRIAL' && nextBillingDate) {
    const daysUntilEnd = Math.ceil((nextBillingDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    if (daysUntilEnd <= 7 && daysUntilEnd > 0) {
      return 'trial';
    }
  }

  // Only show pastDue for ACTIVE subscriptions where billing date passed
  if (status === 'ACTIVE' && nextBillingDate && nextBillingDate < today) {
    return 'pastDue';
  }

  // Renewal coming soon (within 5 days)
  if (status === 'ACTIVE' && nextBillingDate) {
    const daysUntilRenewal = Math.ceil((nextBillingDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    if (daysUntilRenewal <= 5 && daysUntilRenewal > 0) {
      return 'renewal';
    }
  }

  return null;
}

function getDaysRemaining(dateStr: string | undefined): number {
  if (!dateStr) return 0;
  const date = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.ceil((date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

const alertConfigs = {
  renewal: {
    bgColor: 'bg-amber-500/10 border-amber-500/30',
    iconColor: 'text-amber-500',
    icon: Clock,
    titleKey: 'billing.alert.renewalTitle',
    messageKey: 'billing.alert.renewalMessage',
    buttonKey: 'billing.alert.payNow',
    showDays: true,
  },
  pastDue: {
    bgColor: 'bg-red-500/10 border-red-500/30',
    iconColor: 'text-red-500',
    icon: AlertTriangle,
    titleKey: 'billing.alert.pastDueTitle',
    messageKey: 'billing.alert.pastDueMessage',
    buttonKey: 'billing.alert.payNow',
    showDays: true,
  },
  suspended: {
    bgColor: 'bg-red-500/10 border-red-500/30',
    iconColor: 'text-red-500',
    icon: AlertTriangle,
    titleKey: 'billing.alert.suspendedTitle',
    messageKey: 'billing.alert.suspendedMessage',
    buttonKey: 'billing.alert.reactivate',  // Reactivate button for suspended subscriptions
    showDays: false,
  },
  expired: {
    bgColor: 'bg-orange-500/10 border-orange-500/30',
    iconColor: 'text-orange-500',
    icon: AlertTriangle,
    titleKey: 'billing.alert.expiredTitle',
    messageKey: 'billing.alert.expiredMessage',
    buttonKey: 'billing.alert.subscribe',
    showDays: false,
  },
  trial: {
    bgColor: 'bg-blue-500/10 border-blue-500/30',
    iconColor: 'text-blue-500',
    icon: Clock,
    titleKey: 'billing.alert.trialEndingTitle',
    messageKey: 'billing.alert.trialEndingMessage',
    buttonKey: 'billing.alert.subscribe',
    showDays: true,
  },
};

export function PaymentAlertBanner({ subscription, orgId, onPaymentInitiated }: PaymentAlertBannerProps) {
  const { t } = useTranslation('core');
  const [isDismissed, setIsDismissed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  const user = useAuthStore((s) => s.user);
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';

  // SuperAdmin uses admin endpoints; org users use /billing/my-subscription endpoints
  const initiatePaymentAdmin = useInitiatePayment();
  const initiatePaymentOrg = useMySubscriptionPay();
  const checkPaymentStatus = useCheckPaymentStatus();

  const alertType = getAlertType(subscription);

  // Determine the correct invoice link based on role
  const invoiceLinkBase = isSuperAdmin
    ? '/superadmin/invoices'
    : '/venta/billing/invoices';

  // Check payment status on mount if there's a pending/processing payment
  useEffect(() => {
    const pendingPaymentId = subscription?.pendingPaymentId;
    const pendingStatus = subscription?.pendingPaymentStatus;

    if (pendingPaymentId && (pendingStatus === 'PENDING' || pendingStatus === 'PROCESSING')) {
      setIsCheckingStatus(true);
      setStatusMessage(t('billing.alert.checkingPaymentStatus', 'Checking payment status...'));

      checkPaymentStatus.mutate(pendingPaymentId, {
        onSuccess: (result) => {
          setIsCheckingStatus(false);
          if (result.status === 'COMPLETED') {
            setStatusMessage(t('billing.alert.paymentCompleted', 'Payment completed! Refreshing...'));
            // The query invalidation in the hook will refresh subscription data
          } else if (result.status === 'FAILED' || result.status === 'CANCELLED') {
            setStatusMessage(null);
            // Payment failed/cancelled - user can try again
          } else {
            // Still pending/processing - show waiting message
            setStatusMessage(t('billing.alert.paymentProcessing', 'Payment is being processed. Please wait...'));
          }
        },
        onError: () => {
          setIsCheckingStatus(false);
          setStatusMessage(null);
          // Silently fail - user can still click Pay button
        },
      });
    }
  }, [subscription?.pendingPaymentId, subscription?.pendingPaymentStatus]);

  // Don't render if no alert or dismissed
  if (!alertType || isDismissed) {
    return null;
  }

  const config = alertConfigs[alertType];
  const Icon = config.icon;
  const daysRemaining = subscription?.nextBillingDate
    ? getDaysRemaining(subscription.nextBillingDate)
    : 0;

  // Grace period: nextBillingDate + gracePeriodDays → compute remaining grace days
  const gracePeriodDays = subscription?.gracePeriodDays ?? 7;
  const graceRemaining = alertType === 'pastDue' && subscription?.nextBillingDate
    ? (() => {
        const billingDate = new Date(subscription.nextBillingDate!);
        const graceEnd = new Date(billingDate.getTime() + gracePeriodDays * 24 * 60 * 60 * 1000);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return Math.max(0, Math.ceil((graceEnd.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)));
      })()
    : 0;

  const isPaymentPending = initiatePaymentAdmin.isPending || initiatePaymentOrg.isPending;

  const handlePayNow = async () => {
    setError(null);
    try {
      let response;
      if (isSuperAdmin) {
        response = await initiatePaymentAdmin.mutateAsync({ orgId });
      } else {
        response = await initiatePaymentOrg.mutateAsync({});
      }

      if (response.success && response.redirectUrl) {
        if (onPaymentInitiated) {
          onPaymentInitiated(response.redirectUrl);
        } else {
          // Redirect to payment gateway
          window.location.href = response.redirectUrl;
        }
      } else {
        setError(response.message || t('billing.alert.paymentError'));
      }
    } catch (err: unknown) {
      // Extract detailed error message from API response
      if (isApiError(err)) {
        setError(err.message);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError(t('billing.alert.paymentError'));
      }
    }
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    if (currency === 'USD') return `$${amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
    return `${amount.toLocaleString()} ${currency}`;
  };

  return (
    <div className={`border rounded-lg p-4 ${config.bgColor}`}>
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className={`flex-shrink-0 ${config.iconColor}`}>
          <Icon className="w-5 h-5" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-foreground">
            {t(config.titleKey)}
          </h3>
          <p className="text-sm text-muted mt-1">
            {t(config.messageKey, {
              days: config.showDays ? Math.abs(daysRemaining) : undefined,
              amount: subscription ? formatCurrency(subscription.totalUsd, subscription.currency) : '',
              graceDays: graceRemaining,
            })}
          </p>

          {/* Grace period countdown badge for past due */}
          {alertType === 'pastDue' && graceRemaining > 0 && (
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              marginTop: '8px',
              padding: '6px 14px',
              background: 'rgba(239, 68, 68, 0.12)',
              border: '1px solid rgba(239, 68, 68, 0.25)',
              borderRadius: 'var(--radius-md, 5px)',
              fontSize: '13px',
              fontWeight: 600,
              color: 'var(--color-error, #EF4444)',
            }}>
              <Clock className="w-4 h-4" />
              {t('billing.alert.graceRemaining', { days: graceRemaining, count: graceRemaining })}
            </div>
          )}

          {/* Invoice number with link */}
          {subscription?.pendingInvoiceNumber && (
            <div className="mt-2 flex items-center gap-2 text-sm">
              <FileText className="w-4 h-4 text-muted" />
              <span className="text-muted">{t('billing.alert.invoice')}:</span>
              <Link
                to={`${invoiceLinkBase}/${subscription.pendingInvoiceId}`}
                className="text-primary hover:underline font-medium inline-flex items-center gap-1"
              >
                {subscription.pendingInvoiceNumber}
                <ExternalLink className="w-3 h-3" />
              </Link>
            </div>
          )}

          {/* Error message */}
          {error && (
            <p className="text-sm text-red-500 mt-2">{error}</p>
          )}

          {/* Status message when checking payment */}
          {statusMessage && (
            <p className="text-sm text-blue-500 mt-2 flex items-center gap-2">
              <RefreshCw className="w-4 h-4 animate-spin" />
              {statusMessage}
            </p>
          )}

          {/* Action button */}
          <div className="mt-3 flex items-center gap-3">
            <button
              onClick={handlePayNow}
              disabled={isPaymentPending || isCheckingStatus || !!statusMessage}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
            >
              {isPaymentPending || isCheckingStatus ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {isCheckingStatus ? t('billing.alert.checking', 'Checking...') : t('common.processing')}
                </>
              ) : (
                <>
                  <CreditCard className="w-4 h-4" />
                  {t(config.buttonKey)}
                  <ChevronRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </div>

        {/* Dismiss button (for non-critical alerts) */}
        {alertType === 'renewal' && (
          <button
            onClick={() => setIsDismissed(true)}
            className="flex-shrink-0 p-1 rounded hover:bg-white/10"
            title={t('common.dismiss')}
          >
            <X className="w-4 h-4 text-muted" />
          </button>
        )}
      </div>
    </div>
  );
}

export default PaymentAlertBanner;
